import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Form, Button, InputGroup, Alert, Spinner, Card, Row, Col } from '@/components/ui';
import { workIdService } from '../../../services/workIdService';
import type { WorkIdDetail, WorkIdFilterOptions } from '../../../services/workIdService';
import type { WorkIdRecord } from '../workIdService';
import ResponsiveModal from '../../../components/ResponsiveModal';
import TaskModal from '../../../components/QPTaskModal';
import RoutineTaskModal from '../../../components/RoutineTaskModal';
import { taskService } from '../../../services/taskService';
import { areasService } from '../../../services/areasService';
import { ForemanArea } from '../../../types/ForemanArea';
import { ContractArea } from '../../../types/ContractArea';
import { Task } from '../../../types/Task';
import { RoutineTask } from '../../../types/RoutineTask';
import {
  getQuantifiedInitialDataFromWorkId,
  getRoutineInitialDataFromWorkId,
} from '../utils/workIdToTaskInitialData';

type TaskTypeChoice = 'Quantified' | 'Routine';

type InitialStep = 'assign-existing' | 'create-select-type';

interface WorkIdTransitionToTaskModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdRecord | null;
  onSuccess: () => void;
  /** When set, open directly to this step instead of the choose screen */
  initialStep?: InitialStep;
}

const getTaskTypeIcon = (type: TaskTypeChoice) => {
  switch (type) {
    case 'Routine':
      return 'bi-calendar-check';
    case 'Quantified':
      return 'bi-calculator';
    default:
      return 'bi-gear';
  }
};

const getTaskTypeDescription = (type: TaskTypeChoice) => {
  switch (type) {
    case 'Routine':
      return 'A recurring task that follows a regular schedule';
    case 'Quantified':
      return 'A task with specific quantities and measurements';
    default:
      return '';
  }
};

/** Map Work ID filter-options foreman rows to ForemanArea for task modals. */
const mapFilterOptionsToForemanAreas = (
  options: WorkIdFilterOptions['foremanAreas']
): ForemanArea[] =>
  options.map((fa) => ({
    id: fa.id,
    name: fa.name,
    serviceAreaId: fa.serviceAreaId,
    ...(fa.serviceAreaName
      ? {
          serviceArea: {
            id: fa.serviceAreaId,
            name: fa.serviceAreaName,
            foremanAreas: [],
            activities: [],
          },
        }
      : {}),
  }));

const WorkIdTransitionToTaskModal: React.FC<WorkIdTransitionToTaskModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess,
  initialStep,
}) => {
  const [workIdDetail, setWorkIdDetail] = useState<WorkIdDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'choose' | 'assign-existing' | 'create-select-type' | 'task-modal'>('choose');
  const [selectedType, setSelectedType] = useState<TaskTypeChoice | null>(null);
  const [foremanAreas, setForemanAreas] = useState<ForemanArea[]>([]);
  const [contractAreas, setContractAreas] = useState<ContractArea[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [assignComments, setAssignComments] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [taskSearchResults, setTaskSearchResults] = useState<Task[]>([]);
  const [taskSearching, setTaskSearching] = useState(false);
  const [taskSearchOpen, setTaskSearchOpen] = useState(false);
  const taskSearchRef = useRef<HTMLDivElement>(null);

  const fetchWorkIdDetail = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      const detail = await workIdService.getById(id);
      setWorkIdDetail(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load work ID details');
    } finally {
      setLoading(false);
    }
  }, []);

  /** RLS-scoped foreman areas from Work ID filter-options, narrowed to the Work ID service area when known. */
  const loadScopedForemanAreas = useCallback(async (detail: WorkIdDetail | null) => {
    try {
      const options = await workIdService.getFilterOptions();
      const serviceAreaId = detail?.foremanArea?.serviceArea?.id;
      const filtered =
        serviceAreaId != null
          ? options.foremanAreas.filter((fa) => fa.serviceAreaId === serviceAreaId)
          : options.foremanAreas;
      setForemanAreas(mapFilterOptionsToForemanAreas(filtered));
    } catch {
      setForemanAreas([]);
    }
  }, []);

  const loadContractAreas = useCallback(async (foremanAreaId: number) => {
    try {
      const areas = await areasService.getContractAreas(foremanAreaId);
      setContractAreas(areas as ContractArea[]);
    } catch {
      setContractAreas([]);
    }
  }, []);

  useEffect(() => {
    if (visible && workId?.id) {
      fetchWorkIdDetail(workId.id);
    }
  }, [visible, workId?.id, fetchWorkIdDetail]);

  useEffect(() => {
    if (!visible) return;
    loadScopedForemanAreas(workIdDetail);
  }, [visible, workIdDetail, loadScopedForemanAreas]);

  useEffect(() => {
    if (workIdDetail?.foremanAreaId) {
      loadContractAreas(workIdDetail.foremanAreaId);
    } else {
      setContractAreas([]);
    }
  }, [workIdDetail?.foremanAreaId, loadContractAreas]);

  useEffect(() => {
    if (!visible) {
      setStep('choose');
      setSelectedType(null);
      setError(null);
      setSelectedTask(null);
      setAssignComments('');
      setTaskSearchQuery('');
      setTaskSearchResults([]);
      setTaskSearchOpen(false);
      setForemanAreas([]);
      setContractAreas([]);
    } else if (initialStep) {
      setStep(initialStep);
    }
  }, [visible, initialStep]);

  useEffect(() => {
    if (step !== 'assign-existing') return;
    let active = true;
    const run = async () => {
      const q = taskSearchQuery.trim();
      setTaskSearching(true);
      try {
        const limit = 15;
        const results = await taskService.searchAssignableTasks(q, limit);
        if (active) setTaskSearchResults(results);
      } catch {
        if (active) setTaskSearchResults([]);
      } finally {
        if (active) setTaskSearching(false);
      }
    };
    // Fetch immediately when opening with empty query (show assignable tasks right away)
    const delay = taskSearchQuery.trim().length === 0 ? 0 : 300;
    const t = setTimeout(run, delay);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [step, taskSearchQuery]);

  useEffect(() => {
    if (step !== 'assign-existing') return;
    const handleClickOutside = (e: MouseEvent) => {
      if (taskSearchRef.current && !taskSearchRef.current.contains(e.target as Node)) {
        setTaskSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [step]);

  const detailReady = !loading && !!workIdDetail;

  const handleTypeSelect = (type: TaskTypeChoice) => {
    if (!detailReady) return;
    setSelectedType(type);
    setStep('task-modal');
  };

  const handleTaskSaved = async (task: Task | RoutineTask) => {
    if (!workId?.id) return;
    const taskId = (task as Task).id ?? (task as RoutineTask).id;
    if (!taskId) return;
    setSubmitting(true);
    setError(null);
    try {
      await workIdService.assign(workId.id, taskId);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Task created but failed to assign Work ID. You can assign manually.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignExisting = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workId?.id) return;
    if (!selectedTask?.id) {
      setError('Please select a task.');
      return;
    }
    const assignableStatuses = ['PROPOSED', 'PLANNED', 'ACTIVE', 'ACTIVE_MARKED_AS_COMPLETE'];
    const taskStatus = (selectedTask.status as string)?.toUpperCase?.() || '';
    if (!assignableStatuses.includes(taskStatus)) {
      setError('Only Planned, Proposed, or Active tasks can be assigned.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await workIdService.assign(workId.id, selectedTask.id, assignComments || undefined);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign work ID');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setStep('create-select-type');
    setSelectedType(null);
  };

  const handleBackToChoose = () => {
    setStep('choose');
    setSelectedType(null);
  };

  const quantifiedInitialData = workIdDetail ? getQuantifiedInitialDataFromWorkId(workIdDetail) : null;
  const routineInitialData = workIdDetail ? getRoutineInitialDataFromWorkId(workIdDetail) : null;

  if (step === 'assign-existing') {
    return (
      <ResponsiveModal
        isOpen={visible}
        onClose={onClose}
        title={`Assign Work ID ${workId?.workIdentificationId ?? workId?.id ?? ''} to Task`}
        size="medium"
      >
        <Form onSubmit={handleAssignExisting} className="tw-pb-4">
          {error && (
            <Alert variant="danger" className="tw-py-2">
              {error}
            </Alert>
          )}
          <Form.Group className="tw-mb-3" ref={taskSearchRef}>
            <Form.Label>Task (search by task number, name, or description)</Form.Label>
            <div className="tw-relative">
              <InputGroup>
                <InputGroup.Text>
                  <i className="bi bi-search" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Type to search or select from list..."
                  value={selectedTask && !taskSearchOpen
                    ? `${selectedTask.taskNumber ?? selectedTask.id} - ${selectedTask.name ?? 'Unnamed'} (${selectedTask.status ?? '—'})`
                    : taskSearchQuery}
                  onChange={(e) => {
                    setTaskSearchQuery(e.target.value);
                    setTaskSearchOpen(true);
                    if (selectedTask) setSelectedTask(null);
                  }}
                  onFocus={() => setTaskSearchOpen(true)}
                  autoComplete="off"
                />
                {selectedTask && !taskSearchOpen && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setSelectedTask(null);
                      setTaskSearchQuery('');
                      setTaskSearchOpen(true);
                    }}
                    title="Change selection"
                  >
                    <i className="bi bi-x" />
                  </Button>
                )}
              </InputGroup>
              {taskSearchOpen && (
                <div
                  className="tw-border tw-border-line tw-rounded tw-mt-1 tw-bg-surface tw-shadow-sm tw-w-full"
                  style={{ maxHeight: 320, overflowY: 'auto', overflowX: 'hidden' }}
                >
                  {taskSearching ? (
                    <div className="tw-p-3 tw-text-content-muted tw-text-center tw-text-sm">
                      <Spinner animation="border" size="sm" className="tw-mr-2" aria-hidden="true" />
                      Searching...
                    </div>
                  ) : taskSearchResults.length === 0 ? (
                    <div className="tw-p-3 tw-text-content-muted tw-text-center tw-text-sm">
                      No Planned, Proposed, Active, or Active (Marked as Complete) tasks found.
                    </div>
                  ) : (
                    taskSearchResults.map((task) => (
                      <button
                        key={task.id}
                        type="button"
                        className="tw-block tw-w-full tw-text-left tw-border-0 tw-bg-surface hover:tw-bg-surface-hover tw-text-content tw-py-2 tw-px-3"
                        onClick={() => {
                          setSelectedTask(task);
                          setTaskSearchQuery('');
                          setTaskSearchOpen(false);
                        }}
                      >
                        {task.taskNumber ?? task.id} - {task.name ?? 'Unnamed'} ({task.status ?? '—'})
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <Form.Text className="tw-text-content-muted">
              Only Planned, Proposed, Active, and Active (Marked as Complete) tasks are shown.
            </Form.Text>
          </Form.Group>
          <Form.Group className="tw-mb-3">
            <Form.Label>Comments (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={assignComments}
              onChange={(e) => setAssignComments(e.target.value)}
              placeholder="Optional comments"
            />
          </Form.Group>
          <div className="tw-flex tw-gap-2 tw-justify-end">
            <Button variant="outline-secondary" onClick={() => (initialStep ? onClose() : setStep('choose'))} disabled={submitting}>
              Back
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? 'Assigning…' : 'Assign'}
            </Button>
          </div>
        </Form>
      </ResponsiveModal>
    );
  }

  if (step === 'create-select-type') {
    return (
      <Modal show={visible} onHide={onClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="tw-text-content-muted tw-text-sm tw-mb-3">
            Select the task type for the new task.
          </p>
          {loading || !workIdDetail ? (
            <div className="tw-text-center tw-py-4">
              <Spinner animation="border" className="tw-text-primary" aria-hidden="true" />
              <p className="tw-mt-2 tw-mb-0 tw-text-content-muted">Loading work ID details…</p>
            </div>
          ) : error ? (
            <Alert variant="danger" className="tw-mb-0">{error}</Alert>
          ) : (
            <Row>
              {(['Quantified', 'Routine'] as TaskTypeChoice[]).map((type) => (
                <Col md={6} key={type}>
                  <Card
                    className="tw-h-full"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleTypeSelect(type)}
                  >
                    <Card.Body>
                      <div className="tw-flex tw-items-center tw-mb-2">
                        <i className={`bi ${getTaskTypeIcon(type)} tw-text-xl tw-mr-2`} />
                        <Card.Title as="h5" className="tw-mb-0">{type} Task</Card.Title>
                      </div>
                      <Card.Text className="tw-text-content-muted tw-text-sm tw-mb-0">{getTaskTypeDescription(type)}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!initialStep && (
            <Button variant="outline-secondary" onClick={handleBackToChoose}>
              Back
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  if (step === 'task-modal' && selectedType) {
    if (!detailReady) {
      return (
        <Modal show={visible} onHide={onClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Create New Task</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="tw-text-center tw-py-4">
              <Spinner animation="border" className="tw-text-primary" aria-hidden="true" />
              <p className="tw-mt-2 tw-mb-0 tw-text-content-muted">Loading work ID details…</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleBack}>
              Back
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      );
    }

    const commonProps = {
      visible,
      onClose: () => {
        handleBack();
        onClose();
      },
      onTaskSaved: handleTaskSaved,
    };

    if (selectedType === 'Quantified') {
      return (
        <>
          {error && (
            <Alert variant="danger" className="tw-fixed tw-top-0 tw-left-1/2 -tw-translate-x-1/2 tw-mt-3 tw-z-30">
              {error}
            </Alert>
          )}
          <TaskModal
            {...commonProps}
            onClose={handleBack}
            task={undefined}
            foremanAreas={foremanAreas}
            taskType="Quantified"
            initialContractAreas={contractAreas}
            project={undefined}
            defaultStatus="PLANNED"
            initialDataFromWorkId={quantifiedInitialData ?? undefined}
          />
        </>
      );
    }

    if (selectedType === 'Routine') {
      return (
        <>
          {error && (
            <Alert variant="danger" className="tw-fixed tw-top-0 tw-left-1/2 -tw-translate-x-1/2 tw-mt-3 tw-z-30">
              {error}
            </Alert>
          )}
          <RoutineTaskModal
            visible={visible}
            onClose={handleBack}
            onTaskSaved={(task) => handleTaskSaved(task as unknown as Task)}
            task={undefined}
            project={undefined}
            defaultStatus="PLANNED"
            initialDataFromWorkId={routineInitialData ?? undefined}
          />
        </>
      );
    }
  }

  return (
    <Modal show={visible} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Transition Work ID to Task</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="tw-text-center tw-py-4">
            <Spinner animation="border" className="tw-text-primary" aria-hidden="true" />
            <p className="tw-mt-2 tw-mb-0 tw-text-content-muted">Loading work ID details…</p>
          </div>
        ) : error ? (
          <Alert variant="danger" className="tw-mb-0">{error}</Alert>
        ) : (
          <>
            <p className="tw-text-content-muted tw-text-sm tw-mb-3">
              Choose how you want to assign this Work ID to a task.
            </p>
            <div className="tw-flex tw-flex-col tw-gap-2">
              <Button
                variant="outline-primary"
                size="lg"
                className="tw-flex tw-items-center tw-gap-2 tw-py-3"
                onClick={() => setStep('assign-existing')}
              >
                <i className="bi bi-link-45deg tw-text-xl" />
                <div className="tw-text-left">
                  <div className="tw-font-semibold">Assign to Existing Task</div>
                  <small className="tw-text-content-muted">Link this Work ID to an existing Planned, Proposed, or Active task</small>
                </div>
              </Button>
              <Button
                variant="outline-primary"
                size="lg"
                className="tw-flex tw-items-center tw-gap-2 tw-py-3"
                onClick={() => setStep('create-select-type')}
              >
                <i className="bi bi-plus-circle tw-text-xl" />
                <div className="tw-text-left">
                  <div className="tw-font-semibold">Create New Task and Assign</div>
                  <small className="tw-text-content-muted">Create a new Quantified or Routine task and assign this Work ID</small>
                </div>
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WorkIdTransitionToTaskModal;
